import { useState } from 'react';
import { clsx } from 'clsx';
import { Badge, Button } from '@tkpod/ui';

export interface CategoryNode {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  imageUrl: string | null;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  childCount: number;
  templateCount: number;
  children: CategoryNode[];
}

interface CategoryTreeProps {
  categories: CategoryNode[];
  onEdit: (category: CategoryNode) => void;
  onArchive: (category: CategoryNode) => void;
  onDelete: (category: CategoryNode) => void;
}

export function CategoryTree({ categories, onEdit, onArchive, onDelete }: CategoryTreeProps) {
  return (
    <div className="divide-y divide-border">
      {categories.map((cat) => (
        <CategoryTreeNode
          key={cat.id}
          node={cat}
          depth={0}
          onEdit={onEdit}
          onArchive={onArchive}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

function CategoryTreeNode({
  node,
  depth,
  onEdit,
  onArchive,
  onDelete,
}: {
  node: CategoryNode;
  depth: number;
  onEdit: (category: CategoryNode) => void;
  onArchive: (category: CategoryNode) => void;
  onDelete: (category: CategoryNode) => void;
}) {
  const hasChildren = node.children.length > 0;
  const [expanded, setExpanded] = useState(depth === 0);

  return (
    <div>
      <div
        className={clsx(
          'flex items-center gap-3 py-3 pr-4 hover:bg-surface-secondary/50 transition-colors',
        )}
        style={{ paddingLeft: `${depth * 1.5 + 1}rem` }}
      >
        {/* Expand/Collapse toggle */}
        <button
          type="button"
          onClick={() => hasChildren && setExpanded(!expanded)}
          className={clsx(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors',
            hasChildren
              ? 'text-text-secondary hover:bg-surface-tertiary hover:text-text-primary cursor-pointer'
              : 'text-transparent cursor-default',
          )}
          tabIndex={hasChildren ? 0 : -1}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          <svg
            className={clsx(
              'h-4 w-4 transition-transform duration-200',
              expanded && 'rotate-90',
            )}
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
        </button>

        {/* Category image or fallback icon */}
        {node.imageUrl ? (
          <img
            src={node.imageUrl}
            alt={node.name}
            className="h-8 w-8 shrink-0 rounded-md object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
            </svg>
          </div>
        )}

        {/* Name + slug */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-text-primary truncate">{node.name}</span>
            <span className="text-xs text-text-tertiary truncate">/{node.slug}</span>
          </div>
        </div>

        {/* Template count badge */}
        {node.templateCount > 0 && (
          <Badge variant="primary" size="sm">
            {node.templateCount} template{node.templateCount !== 1 ? 's' : ''}
          </Badge>
        )}

        {/* Active/Inactive badge */}
        <Badge variant={node.isActive ? 'success' : 'default'} size="sm" dot>
          {node.isActive ? 'Active' : 'Inactive'}
        </Badge>

        {/* Actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          <Button size="sm" variant="ghost" onClick={() => onEdit(node)}>
            Edit
          </Button>
          {node.isActive && (
            <button
              type="button"
              onClick={() => onArchive(node)}
              className="inline-flex items-center rounded-md border border-warning-300 bg-warning-50 px-2.5 py-1 text-xs font-medium text-warning-700 transition-colors hover:bg-warning-100"
            >
              Archive
            </button>
          )}
          <button
            type="button"
            onClick={() => onDelete(node)}
            className="inline-flex items-center rounded-md border border-danger-300 bg-danger-50 px-2.5 py-1 text-xs font-medium text-danger-700 transition-colors hover:bg-danger-100"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <CategoryTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              onEdit={onEdit}
              onArchive={onArchive}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
